import type {
  CodeItemAttributeDefinition,
  CodeItemAttributeSchemaScope,
  CodeItemAttributeType,
  CodeItemGroup,
  CodeRow,
} from '../types/codeIndex'
import { normalizeRowGroupName } from './codeIndexUtils'

const ATTRIBUTE_TYPES = new Set<CodeItemAttributeType>(['numeric', 'text', 'enumerated'])

export function normalizeAttributeType(value: string | null | undefined): CodeItemAttributeType {
  const normalizedValue = String(value || '').trim().toLowerCase()
  return ATTRIBUTE_TYPES.has(normalizedValue as CodeItemAttributeType)
    ? normalizedValue as CodeItemAttributeType
    : 'text'
}

export function normalizeAttributeName(value: string | null | undefined): string {
  const normalizedValue = String(value || '').trim()
  if (!normalizedValue) return ''

  return `${normalizedValue.charAt(0).toUpperCase()}${normalizedValue.slice(1)}`
}

export function normalizeAttributeDefinition(attribute: Partial<CodeItemAttributeDefinition> | null | undefined): CodeItemAttributeDefinition | null {
  if (!attribute) return null

  const id = String(attribute.id || '').trim()
  const name = normalizeAttributeName(attribute.name)
  if (!id || !name) return null

  const allowedValues = Array.isArray(attribute.allowedValues)
    ? attribute.allowedValues.map(value => String(value || '').trim()).filter(Boolean)
    : []

  return {
    id,
    name,
    type: normalizeAttributeType(attribute.type),
    unit: String(attribute.unit || '').trim() || null,
    allowedValues,
  }
}

export function normalizeAttributeSchemaScopes(
  schemas: CodeItemAttributeSchemaScope[] | null | undefined
): CodeItemAttributeSchemaScope[] {
  if (!Array.isArray(schemas)) return []

  return schemas
    .map((schema): CodeItemAttributeSchemaScope | null => {
      const scope = schema?.scope === 'rowGroup' ? 'rowGroup' : 'codeItem'
      const group = scope === 'rowGroup' ? normalizeRowGroupName(schema.group) : null
      if (scope === 'rowGroup' && !group) return null

      const attributes = Array.isArray(schema.attributes)
        ? schema.attributes
          .map(attribute => normalizeAttributeDefinition(attribute))
          .filter((attribute): attribute is CodeItemAttributeDefinition => Boolean(attribute))
        : []

      if (attributes.length === 0) return null

      return {
        scope,
        group,
        attributes,
      }
    })
    .filter((schema): schema is CodeItemAttributeSchemaScope => Boolean(schema))
}

export function getCodeItemAttributeDefinitions(group: CodeItemGroup | null | undefined): CodeItemAttributeDefinition[] {
  return normalizeAttributeSchemaScopes(group?.attributeSchemas)
    .find(schema => schema.scope === 'codeItem')
    ?.attributes ?? []
}

export function getRowGroupAttributeDefinitions(
  group: CodeItemGroup | null | undefined,
  rowGroup: string | null | undefined
): CodeItemAttributeDefinition[] {
  const normalizedRowGroup = normalizeRowGroupName(rowGroup)
  if (!normalizedRowGroup) return []

  return normalizeAttributeSchemaScopes(group?.attributeSchemas)
    .find(schema => schema.scope === 'rowGroup' && normalizeRowGroupName(schema.group) === normalizedRowGroup)
    ?.attributes ?? []
}

export function getApplicableAttributeDefinitions(
  group: CodeItemGroup | null | undefined,
  row: CodeRow | null | undefined
): CodeItemAttributeDefinition[] {
  const rowGroupAttributes = getRowGroupAttributeDefinitions(group, row?.group)
  return rowGroupAttributes.length > 0
    ? rowGroupAttributes
    : getCodeItemAttributeDefinitions(group)
}

export function groupHasAttributeSchemas(group: CodeItemGroup | null | undefined): boolean {
  return normalizeAttributeSchemaScopes(group?.attributeSchemas).some(schema => schema.attributes.length > 0)
}

export function hasAnyAttributeSchemas(groups: CodeItemGroup[]): boolean {
  return groups.some(group => groupHasAttributeSchemas(group))
}

export function getAttributeDisplayLabel(attribute: CodeItemAttributeDefinition): string {
  const unit = String(attribute.unit || '').trim()
  return unit ? `${attribute.name} (${unit})` : attribute.name
}

export function normalizeAttributeValues(values: Record<string, unknown> | null | undefined): Record<string, string | null> {
  if (!values || typeof values !== 'object') return {}

  return Object.entries(values).reduce<Record<string, string | null>>((accumulator, [key, value]) => {
    const normalizedKey = String(key || '').trim()
    if (!normalizedKey) return accumulator

    if (value === null || value === undefined) {
      accumulator[normalizedKey] = null
      return accumulator
    }

    const normalizedValue = String(value).trim()
    accumulator[normalizedKey] = normalizedValue || null
    return accumulator
  }, {})
}

export function parseEnumeratedAttributeValues(value: string | null | undefined): string[] {
  const seenValues = new Set<string>()

  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .filter((item) => {
      if (seenValues.has(item)) return false
      seenValues.add(item)
      return true
    })
}

export function formatEnumeratedAttributeValues(
  values: string[],
  allowedValues: string[] = []
): string {
  const selectedValues = parseEnumeratedAttributeValues(values.join(','))
  const selectedValueSet = new Set(selectedValues)
  const orderedAllowedValues = allowedValues
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .filter(item => selectedValueSet.has(item))
  const extraValues = selectedValues.filter(item => !orderedAllowedValues.includes(item))

  return [...orderedAllowedValues, ...extraValues].join(', ')
}

export function pruneAttributeValues(
  values: Record<string, string | null> | null | undefined,
  attributes: CodeItemAttributeDefinition[]
): Record<string, string | null> {
  const validIds = new Set(attributes.map(attribute => attribute.id))
  const normalizedValues = normalizeAttributeValues(values)

  return Object.entries(normalizedValues).reduce<Record<string, string | null>>((accumulator, [key, value]) => {
    if (validIds.has(key)) {
      accumulator[key] = value
    }

    return accumulator
  }, {})
}

export function getPopulatedAttributeCount(rows: CodeRow[], attributeId: string): number {
  return rows.filter(row => {
    const value = normalizeAttributeValues(row.attributeValues)[attributeId]
    return String(value || '').trim().length > 0
  }).length
}

export function getAttributeValueValidationError(
  attribute: CodeItemAttributeDefinition,
  value: string | null | undefined
): string | null {
  const normalizedValue = String(value || '').trim()
  if (!normalizedValue) return null

  if (attribute.type === 'numeric' && !/^-?(?:\d+|\d*\.\d+)$/.test(normalizedValue)) {
    return `${attribute.name} must be numeric.`
  }

  if (attribute.type === 'enumerated') {
    const allowedValues = new Set((attribute.allowedValues || []).map(item => item.trim()))
    const selectedValues = parseEnumeratedAttributeValues(normalizedValue)
    const hasInvalidValue = selectedValues.some(value => !allowedValues.has(value))

    if (allowedValues.size > 0 && hasInvalidValue) {
      return `Select a valid ${attribute.name} value.`
    }
  }

  return null
}

export function buildAttributeSummary(
  attributes: CodeItemAttributeDefinition[],
  values: Record<string, string | null> | null | undefined
): string {
  const normalizedValues = normalizeAttributeValues(values)

  return attributes
    .map(attribute => {
      const value = String(normalizedValues[attribute.id] || '').trim()
      if (!value) return null

      const unit = String(attribute.unit || '').trim()
      const shouldShowUnit = Boolean(unit && unit.toLowerCase() !== attribute.name.toLowerCase())
      return `${attribute.name} ${value}${shouldShowUnit ? ` ${unit}` : ''}`
    })
    .filter((item): item is string => Boolean(item))
    .join(' | ')
}

export function validateAttributeSchemaScopes(schemas: CodeItemAttributeSchemaScope[]): string | null {
  for (const schema of normalizeAttributeSchemaScopes(schemas)) {
    const seenNames = new Set<string>()
    for (const attribute of schema.attributes) {
      const normalizedName = attribute.name.trim().toLowerCase()
      if (seenNames.has(normalizedName)) {
        return `Attribute "${attribute.name}" is duplicated in this scope.`
      }
      seenNames.add(normalizedName)

      if (attribute.type === 'enumerated' && (!attribute.allowedValues || attribute.allowedValues.length === 0)) {
        return `Attribute "${attribute.name}" needs at least one allowed value.`
      }
    }
  }

  return null
}
